import {handleActions} from 'redux-actions';
import {Map, List} from 'immutable';

import * as ContactActions from 'web-app/react/entities/contacts/actions';
import ContactModel from 'web-app/react/entities/contacts/model';
import * as BillPayerActions from 'web-app/react/entities/bill-payers/actions';
import * as AccountItemActions from 'web-app/react/entities/account-items/actions';
import * as KeyWorkerActions from 'web-app/react/profile/child-profile/key-worker/actions';
import * as InvoiceModalActions from 'web-app/react/modals-container/invoice/actions';
import {hasValue} from 'web-app/util/typescript';

import Child from './model';
import * as Actions from './actions';
import * as TypedActions from './typed-actions';
import * as AccidentIncidentReportActions from '../accident-incident-reports/actions';

const updateChildInformation = (state, newChildId, updateFunc) => {
    return state.update(newChildId, Child.default, updateFunc);
};

// In specific situations we want to keep the contacts from state
const updateNewWithExistingContacts = (existing, newChild) => {
    if (existing) {
        return newChild.merge({
            contacts: existing.contacts,
        });
    } else {
        return newChild;
    }
};

export default handleActions(
    {
        [Actions.updateBasicChildInfo.type]: (state, {payload}) => {
            return state.set(payload.child.childId, state.get(payload.child.childId).merge({updating: true}));
        },
        [Actions.updateBasicChildInfoSuccess.type]: (state, {payload}) => {
            return state.update(payload.child.childId, existing =>
                updateNewWithExistingContacts(existing, payload.child),
            );
        },
        [Actions.updateBasicChildInfoFailed.type]: (state, {payload}) => {
            return state.set(payload.child.childId, state.get(payload.child.childId).merge({updating: false}));
        },
        [Actions.fetchBasicChildInfoSuccess.type]: (state, {payload}) => {
            return state.update(payload.child.childId, existing =>
                updateNewWithExistingContacts(existing, payload.child),
            );
        },
        [Actions.addTagSuccess.type]: (state, {payload}) => {
            return state.set(
                payload.childId,
                state.get(payload.childId).merge({
                    tagIds: state.get(payload.childId).tagIds.push(payload.tagId),
                }),
            );
        },
        [Actions.removeTagSuccess.type]: (state, {payload}) => {
            return state.set(
                payload.childId,
                state.get(payload.childId).merge({
                    tagIds: state.get(payload.childId).tagIds.filter(tagId => tagId !== payload.tagId),
                }),
            );
        },
        [Actions.updateTagsSuccess.type]: (state, {payload}) => {
            return state.set(
                payload.childId,
                state.get(payload.childId).merge({
                    tagIds: state
                        .get(payload.childId)
                        .tagIds.filter(tagId =>
                            payload.disableTagIds ? payload.disableTagIds.indexOf(tagId) === -1 : true,
                        )
                        .concat(payload.enableTagIds ? payload.enableTagIds : []),
                }),
            );
        },
        [Actions.updateGroup.type]: (state, {payload}) => {
            return state.set(
                payload.childId,
                state.get(payload.childId).merge({updating: true, groupId: payload.groupId}),
            );
        },
        [Actions.updateGroupSuccess.type]: (state, {payload}) => {
            return state.set(
                payload.child.childId,
                state.get(payload.child.childId).merge({updating: false, groupTitle: payload.child.groupTitle}),
            );
        },
        [Actions.updateGroupFailed.type]: (state, {payload}) => {
            return state.set(payload.childId, state.get(payload.childId).merge({updating: false}));
        },
        [Actions.createChildSuccess.type]: (state, {payload}) => {
            return state.set(payload.child.childId, payload.child);
        },
        [Actions.fetchChildrenSuccess.type]: (state, {payload}) => {
            const childMap = Map(payload.children.map(child => [child.childId, child]));

            if (payload.overrideAll) {
                return childMap;
            }

            // Remove empty children that will get overridden
            const filteredState = state.filter((child, id) => (child.childId ? true : !childMap.has(id)));
            return childMap.merge(filteredState);
        },
        [Actions.fetchSuccess.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, child => {
                const childRecord = payload.child;
                let updatedChild = childRecord.set('napsSummary', payload.napsSummary);
                /**
                 * When opening the child profile from the accidents/incidents list, there's gonna be two request triggered that fetch the child data:
                 * The first action processed will be the AccidentsIncidents.fetchAllForChildSuccess, which will set the accidentIncidentReportIds
                 * The second one will be the Actions.fetchSuccess, which doesn't contain the accidentIncidentReportIds.
                 * Since this action is meant to overwrites the child with the data coming in the payload, we need to make sure we keep the accidentIncidentReportIds if there's any
                 * otherwise the accidents will flicker and disappear from the list in the UI
                 */
                if (child && child.get('accidentIncidentReportIds')) {
                    updatedChild = updatedChild.set(
                        'accidentIncidentReportIds',
                        child.get('accidentIncidentReportIds'),
                    );
                }
                return updatedChild;
            });
        },
        [Actions.emberFetchSuccess.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, currentChildEntity => {
                // Because of other reducers interacting with the child entity
                // we need to update it and not completely overwrite it
                const child = hasValue(currentChildEntity)
                    ? currentChildEntity.mergeDeep(Child.fromAPI(payload.child))
                    : Child.fromAPI(payload.child);
                return child.set('napsSummary', payload.napsSummary);
            });
        },
        [Actions.fetchAllSuccess.type]: (state, {payload}) => {
            const childMap = Map(payload.children.map(child => [child.childId, child]));

            // Remove empty children that will get overridden
            const filteredState = state.filter((child, id) => (child.childId ? true : !childMap.has(id)));
            return childMap.merge(filteredState);
        },
        [Actions.updateKeyWorkersSuccess.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, child =>
                child.merge({
                    keyWorkers: payload.keyWorkers,
                }),
            );
        },
        [Actions.checkInSuccess.type]: (state, {payload}) =>
            updateChildInformation(state, payload.childId, child =>
                child.merge({
                    checkedIn: true,
                    checkinTime: payload.checkinTime,
                }),
            ),
        [Actions.checkOutSuccess.type]: (state, {payload}) =>
            updateChildInformation(state, payload.childId, child =>
                child.merge({
                    checkedIn: false,
                }),
            ),
        [Actions.fetchKikuChildSuccess.type]: (state, {payload}) =>
            updateChildInformation(state, payload.childId, child =>
                child.merge({
                    kikuChild: payload.kikuChild,
                }),
            ),
        [Actions.updateKikuChildSuccess.type]: (state, {payload}) =>
            updateChildInformation(state, payload.childId, child =>
                child.merge({
                    kikuChild: payload.kikuChild,
                }),
            ),
        [Actions.fetchAutoGeneratedChildIdSuccess.type]: (state, {payload}) =>
            updateChildInformation(state, payload.childId, child =>
                child.merge({
                    autoGeneratedId: payload.id,
                }),
            ),
        [Actions.updateAutoGeneratedChildIdSuccess.type]: (state, {payload}) =>
            updateChildInformation(state, payload.childId, child =>
                child.merge({
                    autoGeneratedId: payload.id,
                }),
            ),
        [TypedActions.setRoleInvitationRelationIdsForAChild.type]: (state, {payload}) =>
            updateChildInformation(state, payload.childId, child =>
                child.merge({
                    roleInvitationRelationIds: payload.roleInvitationRelationIds,
                }),
            ),
        [TypedActions.addRoleInvitationRelationIdForAChild.type]: (state, {payload}) =>
            updateChildInformation(state, payload.childId, child =>
                child.merge({
                    roleInvitationRelationIds: [...child.roleInvitationRelationIds, payload.roleInvitationRelationId],
                }),
            ),
        [TypedActions.removeRoleInvitationRelationIdForAChild.type]: (state, {payload}) =>
            updateChildInformation(state, payload.childId, child =>
                child.merge({
                    roleInvitationRelationIds: child.roleInvitationRelationIds.filter(
                        roleInvitationRelationId => roleInvitationRelationId !== payload.roleInvitationRelationId,
                    ),
                }),
            ),
        [InvoiceModalActions.cancelSuccess.type]: (state, {payload}) => {
            const {billPayerId, invoices} = payload;
            const childKey = state.findKey(child => child.billPayerIds.includes(billPayerId));

            if (!childKey) {
                return state;
            }

            const ids = invoices.map(invoice => invoice.id);
            return updateChildInformation(state, childKey, child =>
                child.merge({
                    accountItemIds: child.accountItemIds.unshift(...ids),
                }),
            );
        },
        [AccountItemActions.createInvoiceSuccess.type]: (state, action) => {
            const {billPayerId, invoices} = action.payload;
            const childKey = state.findKey(child => child.billPayerIds.includes(billPayerId));

            if (!childKey) {
                return state;
            }

            const ids = invoices.map(invoice => invoice.id);
            return updateChildInformation(state, childKey, child =>
                child.merge({
                    accountItemIds: child.accountItemIds.unshift(...ids),
                }),
            );
        },
        [AccountItemActions.createInvoiceWithGraphQLSuccess.type]: (state, action) => {
            const {billPayerId, invoices} = action.payload;
            const childKey = state.findKey(child => child.billPayerIds.includes(billPayerId));

            if (!childKey) {
                return state;
            }

            const ids = invoices.map(invoice => invoice.id);
            return updateChildInformation(state, childKey, child =>
                child.merge({
                    accountItemIds: child.accountItemIds.unshift(...ids),
                }),
            );
        },
        [KeyWorkerActions.saveSuccess.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, child =>
                child.merge({
                    keyWorkers: Child.keyWorkersFromAPI(payload.keyWorkers),
                }),
            );
        },
        [ContactActions.fetchChildContacts.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, child => child.merge({fetchingContacts: true}));
        },
        [ContactActions.fetchChildContactsFailed.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, child => child.merge({fetchingContacts: false}));
        },
        [ContactActions.fetchChildContactsSuccess.type]: (state, action) => {
            return updateChildInformation(state, action.payload.childId, child =>
                child.merge({
                    contacts: List(action.payload.relations.keys()),
                    fetchingContacts: false,
                }),
            );
        },
        [ContactActions.updateContactsForChildren.type]: (state, action) => {
            // Here, the type of state.action.payload.updatedContacts is:
            // Map<ChildId, Map<ContactId, Contact>>
            return action.payload.relationMaps.reduce((_state, relationMap, childId) => {
                return updateChildInformation(_state, childId, child =>
                    child.merge({
                        contacts: List(relationMap.keys()),
                        fetchingContacts: false,
                    }),
                );
            }, state);
        },
        [ContactActions.fetchChildContactSuccess.type]: (state, action) => {
            const relation = action.payload;
            const childId = relation.childId;

            return updateChildInformation(state, childId, child => {
                const contactIndex = child.relations.findIndex(contact => contact.relationId === relation.relationId);
                return child.merge({
                    relations:
                        contactIndex > -1
                            ? child.relations.update(contactIndex, contact => contact.merge(relation))
                            : child.relations.push(ContactModel.fromAPI(relation)),
                });
            });
        },
        [ContactActions.deleteChildContactSuccess.type]: (state, action) => {
            return updateChildInformation(state, action.payload.childId, child =>
                child.merge({
                    contacts: child.contacts.filter(cId => cId !== action.payload.relationId),
                }),
            );
        },
        [ContactActions.createChildContactSuccess.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, child =>
                child.merge({
                    contacts: child.contacts.push(payload.contact.relationId),
                }),
            );
        },
        [AccidentIncidentReportActions.fetchAllForChildSuccess.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, child =>
                child.merge({
                    accidentIncidentReportIds: payload.reports.map(report => report.reportId),
                }),
            );
        },
        [AccidentIncidentReportActions.deleteReportSuccess.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, child =>
                child.merge({
                    accidentIncidentReportIds: child.accidentIncidentReportIds.filter(id => id !== payload.reportId),
                }),
            );
        },
        [BillPayerActions.fetchAll.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, child =>
                child.merge({
                    fetchingBillPayers: true,
                }),
            );
        },
        [BillPayerActions.fetchAllSuccess.type]: (state, {payload}) => {
            if (payload.childId) {
                return updateChildInformation(state, payload.childId, child =>
                    child.merge({
                        billPayerIds: List(payload.billPayers.map(bp => bp.billPayerId)),
                        splitConfiguration: payload.splitConfiguration,
                        fetchingBillPayers: false,
                        childBillPayersBehaviors: payload.behaviors,
                    }),
                );
            }
            return state;
        },
        [BillPayerActions.updateSplitSuccess.type]: (state, {payload}) => {
            return updateChildInformation(state, payload.childId, child =>
                child.merge({
                    billPayerIds: payload.splitConfiguration.shares.map(bps => bps.billPayerId),
                    splitConfiguration: payload.splitConfiguration,
                }),
            );
        },
    },
    Map(),
);
